import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

const SEOHead = ({ 
  title, 
  description, 
  path = '', 
  image = 'https://impakt-studio.com/og-image.jpg',
  type = 'website'
}) => {
  const { language } = useLanguage();
  
  const siteUrl = 'https://impakt-studio.com';
  const fullUrl = `${siteUrl}${path}`;
  
  const defaultTitles = {
    fr: 'ImpaKt Studio | Agence Premium de Branding & Stratégie Digitale',
    en: 'ImpaKt Studio | Premium Branding & Digital Strategy Agency'
  };
  
  const defaultDescriptions = {
    fr: 'Transformez votre entreprise en marque premium. Branding, stratégie digitale, visibilité web et croissance. 15+ ans d\'expertise, 150+ projets réalisés.',
    en: 'Transform your business into a premium brand. Branding, digital strategy, web visibility and growth. 15+ years of expertise, 150+ projects completed.'
  };
  
  const finalTitle = title || defaultTitles[language];
  const finalDescription = description || defaultDescriptions[language];
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="utf-8" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Language alternates */}
      <link rel="alternate" hrefLang="fr" href={`${siteUrl}${path}`} />
      <link rel="alternate" hrefLang="en" href={`${siteUrl}${path}`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${path}`} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:site_name" content="ImpaKt Studio" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="ImpaKt Studio" />
      <meta name="keywords" content={language === 'fr' 
        ? 'branding, stratégie digitale, agence marketing, identité visuelle, création logo, site web, SEO, réseaux sociaux, croissance, Lyon, Paris'
        : 'branding, digital strategy, marketing agency, visual identity, logo design, website, SEO, social media, growth, Lyon, Paris'
      } />
      
      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "ImpaKt Studio",
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "description": finalDescription,
          "email": "contact@impakt-studio.com",
          "telephone": "+33975953796",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Lyon",
            "addressCountry": "FR"
          },
          "sameAs": [
            "https://www.linkedin.com/company/impakt-studio",
            "https://www.instagram.com/impaktstudio"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;
